import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  editShiftOccurrence,
  getShiftOccurrences,
  getShifts,
  type ShiftUpdateResponse,
  updateShifts,
} from './apiHelpers';
import type { ShiftFormData } from './ShiftForm';

export const useShifts = ({ includeEnded }: { includeEnded: boolean }) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: ['restaurants', restaurantId, 'shifts', includeEnded],
    queryFn: () => getShifts({ restaurantId, includeEnded }),
    initialData: [],
  });
};

export const useShiftsWithOccurrences = ({
  startDate,
  endDate,
  includeEnded,
}: {
  startDate: string;
  endDate: string;
  includeEnded: boolean;
}) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: [
      'restaurants',
      restaurantId,
      'shift-occurrences',
      startDate,
      endDate,
      includeEnded,
    ],
    queryFn: async () =>
      getShiftOccurrences({
        restaurantId,
        startDate,
        endDate,
        includeEnded,
      }),
    initialData: [],
  });
};

type UpdateShiftOccurrenceData = ShiftFormData & {
  occurrenceDate: string;
  applyToAllSubsequentOccurrences: boolean;
};

export const useUpdateShiftOccurrence = ({
  shiftId,
  onSuccess,
}: {
  shiftId: string;
  onSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: async (data: UpdateShiftOccurrenceData) => {
      // Ignore isRecurring - an occurrence is by definition not recurring
      const { isRecurring: _, ...rest } = data;

      return editShiftOccurrence({
        ...rest,
        shiftId,
        restaurantId,
      });
    },
    onSuccess: () => {
      onSuccess();
      successToast({ message: 'Shift successfully updated' });
      queryClient.invalidateQueries({
        queryKey: ['restaurants', restaurantId, 'shift-occurrences'],
      });
    },
    onError: (error) => {
      errorToast({ message: 'Failed to update Shift' });
      reportAppError(error);
    },
  });
};

export const useUpdateShift = ({
  shiftId,
  onSuccess,
}: {
  shiftId: string;
  onSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: async (
      payload: ShiftFormData & { ignoreWarnings: boolean },
    ) => {
      const { ignoreWarnings, ...formData } = payload;

      return updateShifts(restaurantId, {
        ignoreWarnings,
        createdShifts: [],
        updatedShifts: [
          {
            id: shiftId,
            name: formData.name,
            startDate: formData.startDate,
            endDate: formData.endDate || null,
            repeat: formData.repeat,
            startTime: formData.startTime,
            endTime: formData.endTime,
            floorPlanTableIds: formData.floorPlanTableIds,
          },
        ],
        deletedShiftIds: [],
      });
    },
    onSuccess: (data: ShiftUpdateResponse) => {
      if (data.warnings.length === 0) {
        onSuccess();
        successToast({ message: 'Shift successfully updated' });
        queryClient.invalidateQueries({
          queryKey: ['restaurants', restaurantId, 'shift-occurrences'],
        });
      }
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({ message: 'Failed to update Shift' });
      }
      reportAppError(error);
    },
  });
};

export const useCreateShift = ({
  onSuccess,
}: {
  onSuccess: (response: ShiftUpdateResponse) => void;
}) => {
  const queryClient = useQueryClient();
  const { id: restaurantId } = useRestaurant();

  return useMutation({
    mutationFn: async (data: ShiftFormData) => {
      return updateShifts(restaurantId, {
        ignoreWarnings: true,
        createdShifts: [
          {
            startDate: data.startDate,
            endDate: data.endDate || null,
            repeat: data.repeat,
            name: data.name,
            startTime: data.startTime,
            endTime: data.endTime,
            floorPlanTableIds: data.floorPlanTableIds,
          },
        ],
        updatedShifts: [],
        deletedShiftIds: [],
      });
    },
    onSuccess: (data) => {
      onSuccess(data);
      successToast({ message: 'Shift successfully created' });
      queryClient.invalidateQueries({
        queryKey: ['restaurants', restaurantId, 'shift-occurrences'],
      });
      queryClient.invalidateQueries({
        queryKey: ['restaurants', restaurantId, 'shifts'],
      });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({ message: 'Failed to create Shift' });
      }
      reportAppError(error);
    },
  });
};
